import { useCallback, useMemo } from 'react';
import { CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { ChevronRight, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '@/utils/formatDate';

export interface ConversationHeaderProps {
  messageCount: number;
  recordingLink: string | null;
  createdAt?: string | Date;
}

export const ConversationHeader = ({
  messageCount,
  recordingLink,
  createdAt,
}: ConversationHeaderProps) => {
  const handleCopyRecordingLink = useCallback(async () => {
    if (!recordingLink) return;

    try {
      await navigator.clipboard.writeText(recordingLink);
      toast.success('Recording link copied to clipboard');
    } catch (_error) {
      toast.error('Failed to copy recording link');
    }
  }, [recordingLink]);

  const displayDate = useMemo(() => {
    if (!createdAt) {
      return 'Conversation';
    }

    const dateString =
      typeof createdAt === 'string' ? createdAt : createdAt.toISOString();

    const formatted = formatDate(dateString);
    return formatted === 'Not set' ? 'Conversation' : formatted;
  }, [createdAt]);

  return (
    <div className='flex items-center justify-between'>
      <div className='text-xs text-muted-foreground truncate mr-2'>
        {displayDate}
        <span className='ml-2 text-[10px] text-muted-foreground'>
          ({messageCount} messages)
        </span>
      </div>
      <div className='flex items-center gap-2'>
        {recordingLink && (
          <Button
            type='button'
            variant='outline'
            size='sm'
            className='h-6 px-2 text-xs flex items-center gap-1'
            onClick={handleCopyRecordingLink}
          >
            <Copy className='h-3 w-3' />
            Recording link
          </Button>
        )}
        {messageCount > 0 && (
          <CollapsibleTrigger asChild>
            <Button
              variant='ghost'
              size='sm'
              className='group h-6 px-2 text-xs flex items-center gap-1 data-[state=open]:bg-muted'
            >
              <span>View transcript</span>
              <ChevronRight className='h-3 w-3 transition-transform group-data-[state=open]:rotate-90' />
            </Button>
          </CollapsibleTrigger>
        )}
      </div>
    </div>
  );
};
